import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { Loader2Icon, PauseIcon, PlayIcon, TerminalSquareIcon, XIcon } from "lucide-react";
import { useConsoleApi } from "./useConsoleApi";
import { SEVERITIES, type CaLastBoot, type CaSummaryResp } from "./types";
import { MonoTag, SEV_STYLE, STATUS_STYLE, fmtDuration, fmtNum, timeAgo } from "./utils";
import FindingsTab from "./FindingsTab";
import ResourcesTab from "./ResourcesTab";
import AssetsTab from "./AssetsTab";
import BootsTab from "./BootsTab";

export const LIVE_REFRESH_MS = 5000;
export type CaTab = 'findings' | 'resources' | 'assets' | 'boots';
export type FindingsFilter = { severity?: string; resource?: string };


/**
 * Ticks every second so relative times stay fresh
 */
function useNow(intervalMs = 1000) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), intervalMs);
        return () => clearInterval(id);
    }, [intervalMs]);
    return now;
}


/**
 * Mini histogram of findings per boot (oldest -> newest)
 */
function LastBootsChart({ boots, selectedId, onSelect }: {
    boots: CaLastBoot[];
    selectedId?: string;
    onSelect: (id: string) => void;
}) {
    const max = Math.max(1, ...boots.map((b) => b.total));
    const slots = Math.max(boots.length, 12);
    return (
        <div className="flex flex-col gap-1.5">
            <div className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                Last boots
            </div>
            <div className="flex h-16 items-end gap-[3px]">
                {Array.from({ length: slots - boots.length }).map((_, i) => (
                    <div key={`empty-${i}`} className="h-1 w-2.5 rounded-[1px] bg-muted" />
                ))}
                {boots.map((b) => {
                    const height = Math.max(4, Math.round((b.total / max) * 64));
                    return (
                        <button
                            key={b.id}
                            title={`${b.id} — ${b.total} findings${b.failed ? `, ${b.failed} failed` : ''}\n${new Date(b.startedAt).toLocaleString()}`}
                            onClick={() => onSelect(b.id)}
                            className={cn(
                                'flex w-2.5 flex-col-reverse overflow-hidden rounded-[1px] opacity-80 hover:opacity-100',
                                selectedId === b.id && 'opacity-100 outline outline-1 outline-offset-1 outline-foreground/60',
                            )}
                            style={{ height }}
                        >
                            {SEVERITIES.slice().reverse().map((sev) => {
                                const n = b.counts[sev];
                                if (!n || !b.total) return null;
                                return <div key={sev} className={SEV_STYLE[sev].bar} style={{ height: `${(n / b.total) * 100}%` }} />;
                            })}
                        </button>
                    );
                })}
            </div>
            <div className="flex justify-between text-2xs text-muted-foreground">
                <span>older</span>
                <span>latest</span>
            </div>
        </div>
    );
}


function Stat({ label, value, className }: { label: string; value: React.ReactNode; className?: string }) {
    return (
        <div className="flex flex-col">
            <span className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
            <span className={cn("font-mono text-sm", className)}>{value}</span>
        </div>
    );
}


/**
 * The "Current boot" card
 */
function BootCard({ data, now, selectedBootId, onSelectBoot, onSeverityClick, onFailedClick }: {
    data: CaSummaryResp;
    now: number;
    selectedBootId?: string;
    onSelectBoot: (id: string) => void;
    onSeverityClick: (sev: string) => void;
    onFailedClick: (resource: string) => void;
}) {
    const boot = data.boot!;
    const isLive = boot.isCurrent;
    const durationMs = boot.bootDurationMs;
    return (
        <div className="rounded-lg border bg-card p-4 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:justify-between">
                <div className="flex min-w-0 flex-1 flex-col gap-4">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <h2 className="text-lg font-semibold">{isLive ? 'Current boot' : 'Boot'}</h2>
                        <span className={cn('text-sm font-semibold uppercase', STATUS_STYLE[boot.status])}>
                            {boot.status}
                        </span>
                        <MonoTag title={boot.pid ? `PID ${boot.pid}` : undefined}>#{boot.id}</MonoTag>
                        <span className="text-sm text-muted-foreground">
                            started {timeAgo(boot.startedAt, now)}
                        </span>
                    </div>

                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-5">
                        <Stat
                            label="Boot duration"
                            value={durationMs !== null
                                ? fmtDuration(durationMs)
                                : boot.status === 'booting' ? `${fmtDuration(now - boot.startedAt)}…` : '--'}
                        />
                        <Stat label="Lines" value={fmtNum(boot.lines)} />
                        <Stat label="Lines / min" value={fmtNum(boot.linesPerMin)} />
                        <Stat
                            label="Resources"
                            value={boot.resourcesFound !== null
                                ? `${boot.resourcesStarted} / ${boot.resourcesFound}`
                                : boot.resourcesStarted}
                        />
                        <Stat label="Oversized assets" value={fmtNum(boot.assetsCount)} />
                    </div>

                    <div className="grid grid-cols-5 gap-2">
                        {SEVERITIES.map((sev) => (
                            <button
                                key={sev}
                                onClick={() => onSeverityClick(sev)}
                                className={cn(
                                    'flex min-w-0 flex-col items-start rounded-md border border-l-4 bg-background/40 px-2 py-2 text-left hover:bg-muted/50 sm:px-3',
                                    SEV_STYLE[sev].border,
                                )}
                            >
                                <span className={cn('max-w-full truncate text-2xs font-semibold uppercase tracking-wider', SEV_STYLE[sev].text)}>
                                    {sev}
                                </span>
                                <span className={cn('font-mono text-xl font-semibold sm:text-2xl', boot.counts[sev] ? SEV_STYLE[sev].text : 'text-muted-foreground')}>
                                    {fmtNum(boot.counts[sev])}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="shrink-0 lg:w-64 lg:border-l lg:pl-4">
                    <LastBootsChart boots={data.lastBoots} selectedId={selectedBootId ?? boot.id} onSelect={onSelectBoot} />
                </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2 border-t pt-3 text-sm">
                {boot.failedResources.length ? (
                    <>
                        <span className="font-semibold text-red-500">
                            {boot.failedResources.length} failed resource{boot.failedResources.length > 1 ? 's' : ''}
                        </span>
                        {boot.failedResources.map((r) => (
                            <MonoTag key={r} className="border-red-500/40 text-red-400" onClick={() => onFailedClick(r)}>
                                {r}
                            </MonoTag>
                        ))}
                    </>
                ) : (
                    <span className="text-muted-foreground">0 failed resources</span>
                )}
                {boot.droppedFindings > 0 && (
                    <span className="ml-auto text-xs text-warning">
                        {fmtNum(boot.droppedFindings)} findings dropped (limit reached)
                    </span>
                )}
            </div>
        </div>
    );
}


/**
 * Console Analyzer page (Vanta)
 */
export default function ConsoleAnalyzerPage() {
    const [isLive, setIsLive] = useState(true);
    const [tab, setTab] = useState<CaTab>('findings');
    const [selectedBoot, setSelectedBoot] = useState<string | undefined>(undefined);
    const [findingsFilter, setFindingsFilter] = useState<FindingsFilter>({});
    const [assetsResource, setAssetsResource] = useState<string | undefined>(undefined);
    const now = useNow();
    const refreshMs = isLive ? LIVE_REFRESH_MS : 0;

    const summary = useConsoleApi<CaSummaryResp>('summary', { boot: selectedBoot }, refreshMs);
    const data = summary.data;
    const bootId = data?.boot?.id;

    const selectBoot = (id: string | undefined) => {
        const isCurrent = id && data?.lastBoots.at(-1)?.id === id && data?.boot?.isCurrent;
        setSelectedBoot(isCurrent ? undefined : id);
        setFindingsFilter({});
        setAssetsResource(undefined);
    };
    const showFindings = (filter: FindingsFilter) => {
        setFindingsFilter(filter);
        setTab('findings');
    };

    return (
        <div className="flex w-full flex-col gap-4">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                    <TerminalSquareIcon className="size-6 text-muted-foreground" />
                    <h1 className="text-2xl font-semibold">Console Analyzer</h1>
                    <span className="whitespace-nowrap text-sm text-muted-foreground">
                        {summary.isValidating && !data ? 'loading…' : data ? `updated ${timeAgo(data.ts, now)}` : ''}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    {selectedBoot && (
                        <Button size="sm" variant="outline" onClick={() => selectBoot(undefined)}>
                            <XIcon className="mr-1 size-4" /> Back to current boot
                        </Button>
                    )}
                    <Button size="sm" variant={isLive ? 'outline' : 'default'} onClick={() => setIsLive(!isLive)}>
                        {isLive
                            ? <><PauseIcon className="mr-1 size-4" /> Pause live</>
                            : <><PlayIcon className="mr-1 size-4" /> Resume live</>}
                    </Button>
                </div>
            </div>

            {summary.error && (
                <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                    Failed to load the console analysis: {(summary.error as Error).message}
                </div>
            )}

            {!data ? (
                !summary.error && (
                    <div className="flex h-48 items-center justify-center text-muted-foreground">
                        <Loader2Icon className="mr-2 size-5 animate-spin" /> Loading…
                    </div>
                )
            ) : !data.boot ? (
                <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">
                    No server boot recorded yet. Start the server and the analysis will show up here.
                </div>
            ) : (
                <>
                    <BootCard
                        data={data}
                        now={now}
                        selectedBootId={selectedBoot}
                        onSelectBoot={selectBoot}
                        onSeverityClick={(severity) => showFindings({ severity })}
                        onFailedClick={(resource) => showFindings({ resource })}
                    />

                    <Tabs value={tab} onValueChange={(v) => setTab(v as CaTab)}>
                        <TabsList className="h-auto flex-wrap justify-start">
                            <TabsTrigger value="findings">
                                Findings <TabCount n={data.tabs.findings} />
                            </TabsTrigger>
                            <TabsTrigger value="resources">
                                By resource <TabCount n={data.tabs.resources} />
                            </TabsTrigger>
                            <TabsTrigger value="assets">
                                Oversized assets <TabCount n={data.tabs.assets} />
                            </TabsTrigger>
                            <TabsTrigger value="boots">
                                Boots <TabCount n={data.tabs.boots} />
                            </TabsTrigger>
                        </TabsList>
                        <TabsContent value="findings">
                            <FindingsTab
                                bootId={bootId!}
                                refreshMs={refreshMs}
                                now={now}
                                filter={findingsFilter}
                                setFilter={setFindingsFilter}
                            />
                        </TabsContent>
                        <TabsContent value="resources">
                            <ResourcesTab
                                bootId={bootId!}
                                refreshMs={refreshMs}
                                now={now}
                                onSelect={(resource) => showFindings({ resource })}
                            />
                        </TabsContent>
                        <TabsContent value="assets">
                            <AssetsTab
                                bootId={bootId!}
                                refreshMs={refreshMs}
                                now={now}
                                resource={assetsResource}
                                setResource={setAssetsResource}
                            />
                        </TabsContent>
                        <TabsContent value="boots">
                            <BootsTab
                                refreshMs={refreshMs}
                                now={now}
                                selectedId={bootId}
                                onSelect={(id) => {
                                    selectBoot(id);
                                    setTab('findings');
                                }}
                            />
                        </TabsContent>
                    </Tabs>
                </>
            )}
        </div>
    );
}

function TabCount({ n }: { n: number }) {
    return (
        <span className="ml-1.5 rounded-sm bg-muted px-1.5 font-mono text-xs text-muted-foreground">
            {fmtNum(n)}
        </span>
    );
}
